import type { AdminOfferLike } from '../utils/offer-pdf';
import { formatPdfCurrencyAmount } from '../lib/currency';
import { OFFER_SALE_TERMS_BODY } from './offer-sale-terms.content';

export interface OfferTermsContext {
  effectiveDate: string;
  offerNumber: string;
  offerDate: string;
  buyer: string;
  vessel: string;
  imoNumber: string;
  goods: string;
  quantity: string;
  currency: string;
  totalPrice: string;
  paymentTerms: string;
  deliveryLocation: string;
  incoterm: string;
  estimatedLeadTime: string;
  offerValidUntil: string;
  warranty: string;
  countryOfOrigin: string;
  productCondition: string;
  specialConditions: string;
}

function fmtDate(val?: string): string {
  if (!val) return '-';
  try {
    return new Date(val).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return val;
  }
}

export function buildOfferTermsContext(offer: AdminOfferLike): OfferTermsContext {
  const meta = offer.metadata || {};
  const terms = meta.terms || {};
  const lineItems = (offer.items?.length ? offer.items : meta.items) || [];
  const totalQty = lineItems.reduce(
    (sum, item) => sum + (Number(item.offeredQty ?? item.requiredQty ?? 0) || 0),
    0
  );
  const qualities = [...new Set(lineItems.map((i) => i.quality).filter(Boolean))];
  const goodsSummary =
    lineItems.length === 0
      ? '-'
      : lineItems.length === 1
        ? lineItems[0].description || 'Marine spare parts'
        : `${lineItems.length} line items — ${meta.category || 'Marine spare parts'}${meta.brand ? ` (${meta.brand})` : ''}`;

  const paymentBits = [terms.creditType, terms.paymentTerm, meta.requiredPaymentTerms].filter(Boolean);
  const specialBits = [
    meta.packingInstructions && `Packing: ${meta.packingInstructions}`,
    meta.requiredCertificates?.length && `Certificates: ${meta.requiredCertificates.join(', ')}`,
    meta.requiredDocuments?.length && `Documents: ${meta.requiredDocuments.join(', ')}`,
    meta.partialQuoteAllowed && `Partial Quote: ${meta.partialQuoteAllowed}`,
    meta.splitOrderAllowed && `Split Order: ${meta.splitOrderAllowed}`,
    meta.partialDeliveryAllowed && `Partial Delivery: ${meta.partialDeliveryAllowed}`,
    meta.remarks && `Remarks: ${meta.remarks}`,
  ].filter(Boolean);

  const createdAt = offer.createdAt || new Date().toISOString();
  let offerValidUntil = fmtDate(meta.quoteDeadline);
  if ((!meta.quoteDeadline || meta.quoteDeadline === '-') && meta.quoteValidityDays) {
    const d = new Date(createdAt);
    d.setDate(d.getDate() + Number(meta.quoteValidityDays) || 0);
    offerValidUntil = fmtDate(d.toISOString());
  }

  return {
    effectiveDate: fmtDate(createdAt),
    offerNumber: offer.quotationNumber || 'PREVIEW',
    offerDate: fmtDate(createdAt),
    buyer: meta.recipientName || meta.recipientEmail || '-',
    vessel: String(meta.vesselName || '-').toUpperCase(),
    imoNumber: meta.imoNumber || '-',
    goods: goodsSummary,
    quantity: totalQty > 0 ? String(totalQty) : '-',
    currency: offer.currency || String(terms.currency || 'USD'),
    totalPrice: formatPdfCurrencyAmount(Number(offer.totalAmount) || 0, offer.currency || String(terms.currency || 'USD')),
    paymentTerms: paymentBits.length ? paymentBits.join(' · ') : '-',
    deliveryLocation: [terms.deliveryTerm, terms.transportTerm, meta.typeOfLogisticContainer].filter(Boolean).join(' · ') || '-',
    incoterm: meta.typeOfIncoterms || terms.transportTerm || '-',
    estimatedLeadTime: fmtDate(meta.leadDate) !== '-' ? fmtDate(meta.leadDate) : terms.deliveryTerm || '-',
    offerValidUntil,
    warranty: terms.insuranceTerm || 'As per manufacturer / supplier warranty where applicable',
    countryOfOrigin: meta.countryOfOrigin || 'As stated per line item / supplier documentation',
    productCondition: qualities.length ? qualities.join(', ') : 'As per Offer specifications',
    specialConditions: specialBits.length ? specialBits.join(' · ') : '-',
  };
}

function buildSection52(ctx: OfferTermsContext): string {
  return `52. IMPORTANT TRANSACTION-SPECIFIC INFORMATION
The Buyer acknowledges that the following information shown on the Offer forms part of the Transaction:

Offer Number: ${ctx.offerNumber}
Offer Date: ${ctx.offerDate}
Buyer: ${ctx.buyer}
Vessel: ${ctx.vessel}
IMO Number: ${ctx.imoNumber}
Goods: ${ctx.goods}
Quantity: ${ctx.quantity}
Currency: ${ctx.currency}
Total Price: ${ctx.totalPrice}
Payment Terms: ${ctx.paymentTerms}
Delivery Location: ${ctx.deliveryLocation}
Incoterm: ${ctx.incoterm}
Estimated Lead Time: ${ctx.estimatedLeadTime}
Offer Valid Until: ${ctx.offerValidUntil}
Warranty: ${ctx.warranty}
Country of Origin: ${ctx.countryOfOrigin}
Product Condition: ${ctx.productCondition}
Special Conditions: ${ctx.specialConditions}

By accepting the Offer electronically, the Buyer accepts both the Transaction-specific information above and these Sale Terms.

CLICKING "ACCEPT OFFER & TERMS" CONSTITUTES ELECTRONIC ACCEPTANCE OF THE OFFER AND THESE SALE TERMS.`;
}

export const OFFER_SALE_TERMS_ROUTE = '/offer-sale-terms';

export function getOfferSaleTermsPageUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${OFFER_SALE_TERMS_ROUTE}`;
  }
  return `https://admin.euroasianngroup.com${OFFER_SALE_TERMS_ROUTE}`;
}

export function buildGenericOfferSaleTermsText(effectiveDate?: string): string {
  const date =
    effectiveDate ||
    new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  const intro = `EUROASIANN — OFFER TERMS AND CONDITIONS OF SALE OF MARINE SPARE PARTS
Effective Date: ${date}

These Offer Terms and Conditions of Sale ("Sale Terms") apply to every commercial offer, quotation, sales offer, order confirmation, proforma invoice, sales invoice, or other transaction issued by Euroasiann Marine Spares Pvt. Ltd. ("Euroasiann") to a customer ("Buyer") for the sale and supply of marine spare parts, equipment, components, consumables, materials, or related goods ("Goods").

By clicking "Accept Offer & Terms", "Accept & Proceed", placing an order, making payment, issuing a purchase order, or otherwise confirming acceptance of an Offer, the Buyer confirms that it has read, understood, and accepted these Sale Terms.

`;

  const genericSection52 = `52. IMPORTANT TRANSACTION-SPECIFIC INFORMATION
The Buyer acknowledges that transaction-specific details (Offer Number, Buyer, Vessel, Goods, pricing, payment terms, delivery, validity, and related conditions) are stated on the individual Offer document issued by Euroasiann.

By accepting an Offer electronically, the Buyer accepts both the transaction-specific information on that Offer and these Sale Terms.

CLICKING "ACCEPT OFFER & TERMS" CONSTITUTES ELECTRONIC ACCEPTANCE OF THE OFFER AND THESE SALE TERMS.`;

  return `${intro}${OFFER_SALE_TERMS_BODY.trim()}\n\n${genericSection52}`;
}

export function buildOfferSaleTermsText(offer: AdminOfferLike): string {
  const ctx = buildOfferTermsContext(offer);
  const intro = `EUROASIANN — OFFER TERMS AND CONDITIONS OF SALE OF MARINE SPARE PARTS
Effective Date: ${ctx.effectiveDate}

These Offer Terms and Conditions of Sale ("Sale Terms") apply to every commercial offer, quotation, sales offer, order confirmation, proforma invoice, sales invoice, or other transaction issued by Euroasiann Marine Spares Pvt. Ltd. ("Euroasiann") to a customer ("Buyer") for the sale and supply of marine spare parts, equipment, components, consumables, materials, or related goods ("Goods").

By clicking "Accept Offer & Terms", "Accept & Proceed", placing an order, making payment, issuing a purchase order, or otherwise confirming acceptance of an Offer, the Buyer confirms that it has read, understood, and accepted these Sale Terms.

`;

  return `${intro}${OFFER_SALE_TERMS_BODY.trim()}\n\n${buildSection52(ctx)}`;
}

export const OFFER_TERMS_DISCLAIMER = `By clicking "Send Euroasiann Offer", you confirm that the recipient will receive this offer together with the Euroasiann Offer Terms and Conditions of Sale, and that sending the offer constitutes your acceptance to issue the offer on these terms. The recipient's acceptance of the offer will be governed by the same Sale Terms.`;

export const OFFER_TERMS_SEND_MODAL_DISCLAIMER = `By clicking "Send Euroasiann Offer" in this dialog, you confirm that you have reviewed the Euroasiann Offer Terms and Conditions of Sale and that they are automatically accepted. The offer will be sent to the recipient together with these terms.`;